import { useEffect, useRef } from 'react'

/**
 * Hook to implement infinite scroll logic using Intersection Observer API.
 * @param loaderRef - React ref attached to the element at the bottom of the list.
 * @param loadMore - Callback function to trigger when the loader becomes visible.
 * @param enabled - Boolean to enable/disable observation (e.g., stops when no more pages).
 * @param resetKey - Triggers observer reconnection after each successful fetch (e.g. nextPage).
 */
export const useInfiniteScroll = (
  loaderRef: React.RefObject<HTMLElement | null>,
  loadMore: () => Promise<void>,
  enabled: boolean,
  resetKey?: number
) => {
  // Store callback in a ref to prevent observer re-instantiation on every render
  const loadMoreRef = useRef(loadMore)

  useEffect(() => {
    loadMoreRef.current = loadMore
  }, [loadMore])

  useEffect(() => {
    // Exit if observation is disabled or the target element is not available
    if (!enabled || !loaderRef.current) return

    const observer = new IntersectionObserver(
      async (entries) => {
        // Trigger loadMore if the element enters the viewport (plus margin)
        if (!entries[0].isIntersecting) return
        await loadMoreRef.current()
      },
      {
        // Start loading 300px before the element is reached for smoother UX
        rootMargin: '300px',
        // Trigger as soon as the first pixel is visible
        threshold: 0
      }
    )

    // Capture the current target to ensure reliable cleanup
    const currentTarget = loaderRef.current
    observer.observe(currentTarget)

    return () => observer.disconnect()

  // Re-run effect if enabled state, the ref, or resetKey changes
  // resetKey (nextPage) ensures the observer reconnects after each successful fetch,
  // which re-triggers if the sentinel is still visible in the viewport
  }, [enabled, loaderRef, resetKey])
}